import { parseWithZod } from "@conform-to/zod"
import { invariantResponse } from "@epic-web/invariant"
import {
	type LoaderFunctionArgs,
	type ActionFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
} from "react-router"
import {
	data,
	Form,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useFetcher,
	useFetchers,
	useLoaderData,
	useLocation,
	useMatches,
	useSubmit,
} from "react-router"
// import { withSentry } from "@sentry/react"
import { useEffect, useRef } from "react"
import { HoneypotProvider } from "remix-utils/honeypot/react"
import { z } from "zod"
import routes from "#routes/index.ts"
import { NewsletterForm } from "./components/newsletter-form.tsx"
import AdminUserDropdown from "./components/admin/user-dropdown.tsx"
import { GeneralErrorBoundary } from "./components/error-boundary.tsx"
import Navigation from "./components/header/navigation.tsx"
import GretasPatchwork from "./components/logos/gretas-patchwork.tsx"
import { Progress } from "./components/progress-bar.tsx"
import { useToast } from "./components/toaster.tsx"
import { Badge } from "./components/ui/badge.tsx"
import { Button } from "./components/ui/button.tsx"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "./components/ui/dropdown-menu.tsx"
import { Icon, href as iconsHref } from "./components/ui/icon.tsx"
import { Toaster } from "./components/ui/sonner.tsx"
import { TypographyMuted } from "./components/ui/typography.tsx"
import brandmarkScriptStyleSheetUrl from "./styles/brandscript-font.css?url"
import sonnerStyles from "./styles/sonner.css?url"
import tailwindStyleSheetUrl from "./styles/tailwind.css?url"
import { getUserId, logout } from "./utils/auth.server.ts"
import { getCartSessionId } from "./utils/cart.server.ts"
import { ClientHintCheck, getHints, useHints } from "./utils/client-hints.tsx"
import { APP_NAME } from "./utils/constants.ts"
import { getCookiePreferences } from "./utils/cookies-notice.server.ts"
import { prisma } from "./utils/db.server.ts"
import { getEnv } from "./utils/env.server.ts"
import { useIdentifyUser, useTrackAddToCart } from "./utils/analytics-hooks.ts"
import { UmamiScript, useTrackPageview } from "./utils/umami.tsx"
import { honeypot } from "./utils/honeypot.server.ts"
import { generateMeta } from "./utils/meta.ts"
import { combineHeaders, getDomainUrl, getUserImgSrc } from "./utils/misc.tsx"
import { useNonce } from "./utils/nonce-provider.ts"
import { useRequestInfo } from "./utils/request-info.ts"
import { type Theme, setTheme, getTheme } from "./utils/theme.server.ts"
import { makeTimings, time } from "./utils/timing.server.ts"
import { getToast } from "./utils/toast.server.ts"
import { useOptionalUser, useUser } from "./utils/user.ts"
import { CookiesNotice } from "./routes/resources+/cookie-preferences.tsx"
import { WishlistProvider, useWishlist } from "./utils/wishlist-context.tsx"
import { getWishlistItems } from "./utils/wishlist.server.ts"

export const links: LinksFunction = () => {
	return [
		// Preload critical resources first for early discovery
		{ rel: "preload", href: iconsHref, as: "image" },
		// Preload CSS to start download early but don't block rendering
		{ rel: "preload", href: tailwindStyleSheetUrl, as: "style" },
		{ rel: "stylesheet", href: tailwindStyleSheetUrl },
		{ rel: "mask-icon", href: "/favicons/mask-icon.svg" },
		{
			rel: "alternate icon",
			type: "image/png",
			href: "/favicons/favicon-32x32.png",
		},
		{ rel: "apple-touch-icon", href: "/favicons/apple-touch-icon.png" },
		{
			rel: "manifest",
			href: "/site.webmanifest",
			crossOrigin: "use-credentials",
		} as const,
		{ rel: "icon", type: "image/svg+xml", href: "/favicons/favicon.svg" },
		{ rel: "preload", href: sonnerStyles, as: "style" },
		{ rel: "stylesheet", href: sonnerStyles },
		{ rel: "preload", href: brandmarkScriptStyleSheetUrl, as: "style" },
		{ rel: "stylesheet", href: brandmarkScriptStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ error }) => {
	return generateMeta({
		title: error ? "Error | " + APP_NAME : undefined,
	})
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings("root loader")
	let cart

	// If the request is a healthcheck, don't create new cart sessions.
	if (request.headers.get("x-healthcheck") !== "true")
		cart = await getCartSessionId(request)

	const userId = await time(() => getUserId(request), {
		timings,
		type: "getUserId",
		desc: "getUserId in root",
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							firstName: true,
							username: true,
							lastName: true,
							image: { select: { id: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: "find user", desc: "find user in root" },
			)
		: null
	if (userId && !user) {
		console.info("something weird happened")
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: "/" })
	}

	let wishlistItemIds: string[] = []
	if (userId) {
		const wishlist = await prisma.wishlist.upsert({
			where: { userId },
			create: { userId },
			update: {},
		})
		const items = await prisma.wishlistItem.findMany({
			where: { wishlistId: wishlist.id },
			select: { productId: true },
		})
		wishlistItemIds = items.map(item => item.productId)
	}

	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			cartItemsAmount: cart?.cartItemsAmount,
			user,
			wishlistItemIds,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					cookiePreferences: getCookiePreferences(request),
					theme: getTheme(request),
				},
			},
			ENV: getEnv() as Record<string, string>,
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ "Server-Timing": timings.toString() },
				toastHeaders,
				cart?.headers,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		"Server-Timing": loaderHeaders.get("Server-Timing") ?? "",
	}
	return headers
}

const ThemeFormSchema = z.object({
	theme: z.enum(["system", "light", "dark"]),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: ThemeFormSchema,
	})

	invariantResponse(submission.status === "success", "Invalid theme received")

	const { theme } = submission.value

	const responseInit = {
		headers: { "set-cookie": setTheme(theme) },
	}
	return data({ result: submission.reply() }, responseInit)
}

function Document({
	children,
	nonce,
	theme = "light",
	env = {},
	allowIndexing = true,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
	allowIndexing?: boolean
}) {
	return (
		<html
			lang="es"
			className={`${theme} h-full overflow-x-hidden`}
			suppressHydrationWarning
		>
			<head>
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `
						(function() {
							const cookieName = "gp_theme";
							const cookieValue = document.cookie.match(new RegExp("(^| )" + cookieName + "=([^;]+)"));
							let theme = cookieValue ? cookieValue[2] : "light";
							if (theme === "system") {
								theme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
							}
							document.documentElement.classList.toggle("dark", theme === "dark");
						})();
					`,
					}}
				/>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
				<UmamiScript nonce={nonce} data-website-id={env.UMAMI_WEBSITE_ID} />
			</body>
		</html>
	)
}

function Header() {
	const user = useOptionalUser()
	const { cartItemsAmount: initialCartAmount } = useLoaderData<typeof loader>()
	const fetcher = useFetcher<{ cartItemsAmount: number }>()
	const fetchers = useFetchers()

	const cartCountFetcher = useFetcher<{ cartItemsAmount: number }>()

	const cartAmount =
		fetcher.data?.cartItemsAmount ??
		cartCountFetcher.data?.cartItemsAmount ??
		initialCartAmount ??
		0

	const isAddingToCart = fetchers.some(
		f =>
			f.formAction === "/cart" && f.formData?.get("intent") === "add-to-cart",
	)

	useEffect(() => {
		cartCountFetcher.load("/resources/cart-count")
	}, [])

	useEffect(() => {
		if (isAddingToCart) {
			cartCountFetcher.load("/resources/cart-count")
		}
	}, [isAddingToCart])

	return (
		<header className="container my-6 flex justify-between gap-2 md:gap-5">
			<div className="flex flex-1">
				<Navigation />
			</div>

			<Link to={routes.public.base.route()} aria-label="Ir a página de inicio">
				<GretasPatchwork className="max-w-[200px] lg:max-w-[300px]" />
			</Link>

			<div className="flex flex-1 justify-end gap-2">
				<Link
					to={routes.public.cart.route()}
					className="relative"
					aria-label="Carrito de compras"
				>
					{cartAmount && cartAmount > 0 ? (
						<Badge className="absolute -top-1 right-0 !px-1 !py-px !text-[12px]">
							{cartAmount}
						</Badge>
					) : null}
					<Button variant="ghost" size="icon" aria-label="Carrito de compras">
						<Icon name="lucide-shopping-bag" size="md" />
					</Button>
				</Link>
				<div className="hidden items-center gap-3 self-start lg:flex">
					{user ? (
						<UserDropdown />
					) : (
						<Link
							to={routes.public.auth_login.route()}
							aria-label="Iniciar sesión"
						>
							<Button variant="ghost" size="icon" aria-label="Iniciar sesión">
								<Icon name="enter" size="md" />
							</Button>
						</Link>
					)}
				</div>
			</div>
		</header>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	const theme = useTheme()
	const matches = useMatches()
	const isInAdminPage = matches.some(m => m.id.includes("admin+"))
	const allowIndexing = data.ENV.ALLOW_INDEXING !== "false"
	const { userPrefs } = data.requestInfo
	useToast(data.toast)
	useTrackPageview()
	useIdentifyUser()
	useTrackAddToCart()

	useEffect(() => {
		document.documentElement.classList.toggle("dark", theme === "dark")
	}, [theme])

	return (
		<HoneypotProvider {...data.honeyProps}>
			<Document
				nonce={nonce}
				theme={theme}
				env={data.ENV}
				allowIndexing={allowIndexing}
			>
				<div className="relative flex h-screen flex-col justify-between">
					{!isInAdminPage && <Header />}

					<div className="flex-1">
						<Outlet />
					</div>

					{!isInAdminPage && (
						<footer className="container flex flex-col gap-6 pb-5">
						<div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
							<p className="text-sm font-medium text-muted-foreground">
								Suscríbete a nuestro newsletter
							</p>
							<NewsletterForm
								variant="inline"
								listType="newsletter"
							/>
						</div>
						<div className="flex flex-wrap items-center justify-between gap-4">
							<div className="flex flex-col gap-1">
								<Logo />
								<TypographyMuted className="!mt-0">
									&copy; Greta&lsquo;s Patchwork, {new Date().getFullYear()}
								</TypographyMuted>
							</div>
							<div className="flex flex-wrap gap-4">
								<Link
									to="/tos"
									className="text-sm text-muted-foreground hover:text-foreground"
								>
									Términos
								</Link>
								<Link
									to="/privacy"
									className="text-sm text-muted-foreground hover:text-foreground"
								>
									Privacidad
								</Link>
								<Link
									to="/return-policy"
									className="text-sm text-muted-foreground hover:text-foreground"
								>
									Devoluciones
								</Link>
								<Link
									to="/blog"
									className="text-sm text-muted-foreground hover:text-foreground"
								>
									Blog
								</Link>
								<Link
									to="/contact"
									className="text-sm text-muted-foreground hover:text-foreground"
								>
									Contacto
								</Link>
							</div>
							<div className="flex items-center gap-2">
								<Link
									to="/blog/rss.xml"
									className="flex h-8 w-8 items-center justify-center text-muted-foreground hover:text-foreground"
									aria-label="RSS Feed"
								>
									<Icon name="rss">
										<span className="sr-only">RSS Feed</span>
									</Icon>
								</Link>
								<ThemeSwitch
									userPreference={data.requestInfo.userPrefs.theme}
								/>
							</div>
						</div>
						<p className="border-t border-border pt-6 text-center text-xs tracking-wide text-muted-foreground">
							Hecho a mano con{" "}
							<span className="text-red-500">&#10084;</span>
							{" "}en España{" "}
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 600" className="mb-0.5 inline-block h-3 w-auto" aria-label="Bandera de España" role="img">
								<rect width="900" height="600" fill="#c60b1e"/>
								<rect width="900" height="300" y="150" fill="#ffc400"/>
							</svg>
						</p>
						</footer>
					)}

					{!isInAdminPage &&
						(userPrefs.cookiePreferences === null ||
							userPrefs.cookiePreferences?.interacted === false) && (
							<CookiesNotice />
						)}
				</div>
				<Toaster closeButton position="top-right" />
				<Progress />
			</Document>
		</HoneypotProvider>
	)
}

function Logo() {
	return (
		<Link to="/" className="group grid leading-snug">
			<span className="font-light transition group-hover:-translate-x-1	">
				greta's
			</span>
			<span className="font-bold transition group-hover:translate-x-1	">
				patchwork
			</span>
		</Link>
	)
}

function WishlistMerge() {
	const user = useOptionalUser()
	const fetcher = useFetcher<{ success?: boolean }>()
	const hasMerged =
		typeof window !== "undefined"
			? localStorage.getItem("fenix_wishlist_merged")
			: null

	useEffect(() => {
		if (!user) return

		const localWishlistStr = localStorage.getItem("fenix_wishlist")
		if (!localWishlistStr) return

		try {
			const localWishlist = JSON.parse(localWishlistStr)
			if (!Array.isArray(localWishlist) || localWishlist.length === 0) return

			if (!hasMerged) {
				fetcher.submit(
					{ localWishlist: localWishlistStr, intent: "merge" },
					{ method: "POST", action: "/resources/wishlist" },
				)
			}
		} catch {
			console.error("Failed to parse local wishlist")
		}
	}, [user, hasMerged])

	useEffect(() => {
		if (fetcher.data?.success) {
			localStorage.setItem("fenix_wishlist_merged", "true")
			localStorage.removeItem("fenix_wishlist")
			window.location.reload()
		}
	}, [fetcher.data])

	return null
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<WishlistProvider serverWishlistIds={data.wishlistItemIds ?? []}>
			<WishlistMerge />
			<HoneypotProvider {...data.honeyProps}>
				<App />
			</HoneypotProvider>
		</WishlistProvider>
	)
}

export default AppWithProviders

function UserDropdown() {
	const user = useUser()
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)
	return (
		<DropdownMenu>
			<DropdownMenuTrigger className="flex items-center gap-2 rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground shadow-sm hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
				<img
					className="h-8 w-8 rounded-full object-cover"
					alt={user.firstName ?? "Usuario"}
					src={getUserImgSrc(user.image?.id)}
				/>
				<span className="text-body-sm font-bold">{user.firstName}</span>
			</DropdownMenuTrigger>
			<DropdownMenuContent sideOffset={8} align="start">
				<DropdownMenuItem asChild>
					<Link prefetch="intent" to={routes.private.account.route()}>
						<Icon name="avatar">Perfil</Icon>
					</Link>
				</DropdownMenuItem>

				{user.roles.map(role => role.name).includes("admin") && (
					<>
						<DropdownMenuSeparator />

						<AdminUserDropdown />

						<DropdownMenuSeparator />
					</>
				)}

				<DropdownMenuItem
					asChild
					// this prevents the menu from closing before the form submission is completed
					onSelect={event => {
						event.preventDefault()
						submit(formRef.current)
					}}
				>
					<Form action="/logout" method="POST" ref={formRef}>
						<Icon name="exit">
							<button type="submit">Cerrar sesión</button>
						</Icon>
					</Form>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
	const hints = useHints()
	const requestInfo = useRequestInfo()
	return requestInfo.userPrefs.theme ?? hints.theme
}

function ThemeSwitch({ userPreference }: { userPreference?: Theme | null }) {
	const fetcher = useFetcher()

	const mode: Theme | "system" =
		(userPreference as Theme | "system" | null) ?? "system"
	let nextMode: Theme | "system" = "light"
	if (mode === "system") {
		nextMode = "light"
	} else if (mode === "light") {
		nextMode = "dark"
	} else {
		nextMode = "system"
	}
	const modeLabel = {
		light: (
			<Icon name="sun">
				<span className="sr-only">Light</span>
			</Icon>
		),
		dark: (
			<Icon name="moon">
				<span className="sr-only">Dark</span>
			</Icon>
		),
		system: (
			<Icon name="laptop">
				<span className="sr-only">System</span>
			</Icon>
		),
	}

	return (
		<fetcher.Form method="POST" action="/">
			<input type="hidden" name="theme" value={nextMode} />
			<div className="flex gap-2">
				<button
					type="submit"
					className="flex h-8 w-8 cursor-pointer items-center justify-center"
				>
					{modeLabel[mode]}
				</button>
			</div>
		</fetcher.Form>
	)
}

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()
	const location = useLocation()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary
				statusHandlers={{
					404: () => (
						<div className="flex flex-col gap-6">
							<div className="flex flex-col gap-3">
								<h1>No podemos encontrar esta página:</h1>
								<pre className="whitespace-pre-wrap break-all text-body-lg">
									{location.pathname}
								</pre>
							</div>
							<Link to="/" className="text-body-md underline">
								<Icon name="arrow-left">Volver a inicio</Icon>
							</Link>
						</div>
					),
				}}
			/>
		</Document>
	)
}
